import { z } from 'zod';

// Валидация БИН (12 цифр)
export const binSchema = z.string()
    .regex(/^\d{12}$/, 'БИН должен содержать 12 цифр');

// Валидация ИИН (12 цифр)
export const iinSchema = z.string()
    .regex(/^\d{12}$/, 'ИИН должен содержать 12 цифр');

// Валидация казахстанского номера телефона
export const phoneSchema = z.string()
    .regex(/^\+7\d{11}$/, 'Неверный формат телефона (+7XXXXXXXXXXX)');

// Валидация email
export const emailSchema = z.string()
    .email('Неверный формат email');

// Валидация пароля
export const passwordSchema = z.string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Пароль должен содержать хотя бы одну заглавную букву, одну строчную букву и одну цифру');

// Валидация OTP кода
export const otpSchema = z.string()
    .regex(/^\d{4}$/, 'OTP код должен содержать 4 цифры');

// Схема для регистрации клиента
export const clientRegisterSchema = z.object({
    login: phoneSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    firstName: z.string().min(1, 'Имя обязательно'),
    lastName: z.string().min(1, 'Фамилия обязательна'),
    middleName: z.string().optional(),
    phoneNumber: phoneSchema,
    email: emailSchema.optional(),
    iin: iinSchema.optional(),
    position: z.string().optional(),
    companyBin: binSchema.optional(),
}).refine((data) => data.password === data.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
});

// Схема для регистрации модератора
export const moderatorRegisterSchema = z.object({
    login: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    firstName: z.string().min(1, 'Имя обязательно'),
    lastName: z.string().min(1, 'Фамилия обязательна'),
    middleName: z.string().optional(),
    email: emailSchema,
    position: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
});

// Схема для входа
export const loginSchema = z.object({
    login: z.string().min(1, 'Логин обязателен'),
    password: z.string().min(1, 'Пароль обязателен'),
});

// Схема для подтверждения OTP
export const otpVerificationSchema = z.object({
    login: z.string().min(1, 'Логин обязателен'),
    otpCode: otpSchema,
});

// Схема для восстановления пароля
export const forgotPasswordSchema = z.object({
    login: z.string().min(1, 'Логин обязателен'),
});

// Утилиты для форматирования
export const formatPhone = (phone: string): string => {
    // Убираем все нецифровые символы
    const digits = phone.replace(/\D/g, '');

    // Если начинается с 8, заменяем на 7
    if (digits.startsWith('8')) {
        return '+7' + digits.slice(1);
    }

    // Если начинается с 7, добавляем +
    if (digits.startsWith('7')) {
        return '+' + digits;
    }

    // Иначе добавляем +7
    return '+7' + digits;
};

export const formatPhoneDisplay = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('7')) {
        return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
    }
    return phone;
};

// Форматирование номера для WhatsApp API (добавляет 8 после кода страны для Казахстана)
export const formatPhoneForWhatsApp = (phone: string): string => {
    // Убираем все нецифровые символы
    const digits = phone.replace(/\D/g, '');

    // Если начинается с 8 и длина 11 цифр (старый формат Казахстана)
    if (digits.startsWith('8') && digits.length === 11) {
        // Заменяем 8 на 78 для формата WhatsApp
        return '78' + digits.slice(1);
    }

    // Если начинается с 7 и длина 11 цифр (казахстанский номер)
    if (digits.startsWith('7') && digits.length === 11) {
        // Добавляем 8 после 7 для формата WhatsApp
        return '78' + digits.slice(1);
    }

    // Если уже в правильном формате (начинается с 78)
    if (digits.startsWith('78') && digits.length === 12) {
        return digits;
    }

    // Для других случаев возвращаем как есть
    return digits;
};

export const formatBin = (bin: string): string => {
    const digits = bin.replace(/\D/g, '');
    return digits.slice(0, 12);
};

export const formatIin = (iin: string): string => {
    const digits = iin.replace(/\D/g, '');
    return digits.slice(0, 12);
};
